import {
	hashKey,
	type QueryClient,
	type QueryKey,
} from "@tanstack/react-query";
import {
	DATASET_REGISTRY,
	type DatasetId,
	type DatasetScope,
} from "./datasetRegistry";
import {
	addToDehydrateAllowlist,
	removeFromDehydrateAllowlist,
} from "./dehydrateAllowlist";
import { setDatasetMeta } from "./offlineMeta";

export function scopeKey(scope: DatasetScope): string {
	return scope.establishmentId
		? `org:${scope.organizationId}:est:${scope.establishmentId}`
		: `org:${scope.organizationId}`;
}

function estimateBytes(data: unknown): number {
	try {
		return new Blob([JSON.stringify(data)]).size;
	} catch {
		return 0;
	}
}

export async function prepareDataset(
	queryClient: QueryClient,
	datasetId: DatasetId,
	scope: DatasetScope,
	{ keepOnError = false }: { keepOnError?: boolean } = {},
): Promise<{ ok: boolean; overCap?: boolean; error?: string }> {
	const def = DATASET_REGISTRY[datasetId];
	const opts = def.queryOptionsFactory(scope);

	// Add to allowlist BEFORE fetching so the cache persistence triggered by
	// fetchQuery's cache update sees the key and dehydrates it.
	addToDehydrateAllowlist(opts.queryKey);

	const previous = queryClient.getQueryState(opts.queryKey);

	let result: unknown;
	try {
		result = await queryClient.fetchQuery({
			...opts,
			staleTime: 0,
		});
	} catch (e) {
		const error = e instanceof Error ? e.message : String(e);
		// A background refresh must not cost the user the copy they already have.
		// The failed fetch left the query in "error", and the in-memory allowlist
		// is empty after a reload, so only a "success" entry is persisted again.
		if (keepOnError) {
			if (previous?.data !== undefined) {
				queryClient.setQueryData(opts.queryKey, previous.data, {
					updatedAt: previous.dataUpdatedAt,
				});
			}
			return { ok: false, error };
		}
		removeFromDehydrateAllowlist(opts.queryKey);
		await setDatasetMeta({
			datasetId,
			scope: scopeKey(scope),
			lastSyncedAt: Date.now(),
			count: 0,
			estimatedBytes: 0,
			lastError: error,
		});
		return { ok: false, error };
	}

	const typed = result as { overCap: boolean; data: unknown[] };
	if (typed.overCap) {
		removeFromDehydrateAllowlist(opts.queryKey);
		await setDatasetMeta({
			datasetId,
			scope: scopeKey(scope),
			lastSyncedAt: Date.now(),
			count: 0,
			estimatedBytes: 0,
			lastError: "over-cap",
		});
		return { ok: false, overCap: true };
	}

	try {
		await navigator.storage?.persist?.();
	} catch {
		// non-fatal
	}

	await setDatasetMeta({
		datasetId,
		scope: scopeKey(scope),
		lastSyncedAt: Date.now(),
		count: typed.data.length,
		estimatedBytes: estimateBytes(result),
	});

	return { ok: true };
}

/**
 * A dataset is prepared once `prepareDataset` stored its entry, even when the
 * scope holds no rows. An over-cap entry holds no data.
 */
export function isPreparedDataset<
	T extends { overCap: boolean; data: unknown[] },
>(cached: T | undefined): cached is T {
	return !!cached && !cached.overCap;
}

/**
 * Re-prepares every scope of `datasetId` the org has prepared, including one
 * that held no rows, and re-prepares each scope whose first prepare is still
 * in flight once that prepare settles.
 * The offline entries have no `useQuery` observer (readers use `getQueryData`),
 * so `invalidateQueries` would only flag them stale and never refetch them.
 */
export async function refreshPreparedDatasets(
	queryClient: QueryClient,
	datasetId: DatasetId,
	organizationId: string,
): Promise<void> {
	const [orgKey] = DATASET_REGISTRY[datasetId].queryOptionsFactory({
		organizationId,
	}).queryKey;
	const scopes = queryClient.getQueryCache().findAll({ queryKey: [orgKey] });
	const isPrepared = ({ state }: (typeof scopes)[number]) =>
		isPreparedDataset(
			state.data as { overCap: boolean; data: unknown[] } | undefined,
		);
	const reprepare = ({ queryKey }: (typeof scopes)[number]) => {
		const scope = queryKey[1] as
			| Pick<DatasetScope, "establishmentId">
			| undefined;
		return prepareDataset(
			queryClient,
			datasetId,
			{ organizationId, establishmentId: scope?.establishmentId },
			{ keepOnError: true },
		);
	};

	// Classified before any await: a first prepare that settles while the
	// prepared scopes re-prepare must still be re-prepared.
	const prepared = scopes.filter(isPrepared);
	const firstPrepares = scopes.filter(
		({ state }) => state.data === undefined && state.fetchStatus === "fetching",
	);

	for (const query of prepared) {
		try {
			// A fetch already in flight predates this change, and fetchQuery would
			// dedupe onto it, leaving the stale snapshot.
			await queryClient.cancelQueries({
				queryKey: query.queryKey,
				exact: true,
			});
			await reprepare(query);
		} catch {
			// Best-effort: one scope's failure (e.g. IndexedDB unavailable) must not
			// leave the remaining scopes stale.
		}
	}

	for (const query of firstPrepares) {
		try {
			// Cancelling a fetch that holds no data rejects the manual prepare that
			// started it, so wait for it to settle instead. That prepare owns its error.
			await query.promise?.catch(() => undefined);
			if (isPrepared(query)) await reprepare(query);
		} catch {
			// Best-effort, as above.
		}
	}
}

// Datasets whose queued writes declare their org-grain key in the registry's
// `invalidate` effect, meaning "this copy is now stale".
const REFRESHED_ON_REPLAY: readonly DatasetId[] = [
	"animals",
	"paddocks",
	"herds",
];

/**
 * Replay path of `refreshPreparedDatasets`: a replayed write never runs the
 * per-hook `onSuccess`, so the keys its registered `invalidate` effect declares
 * are the only trace of what it changed. Re-prepares each dataset whose
 * org-grain key appears there once, however many keys name it, and ignores
 * every other key.
 */
export async function refreshPreparedDatasetsForKeys(
	queryClient: QueryClient,
	keys: readonly QueryKey[],
): Promise<void> {
	const refreshes = new Map<string, Promise<void>>();
	for (const key of keys) {
		const organizationId = (
			key[0] as { params?: { organizationId?: unknown } } | null
		)?.params?.organizationId;
		if (typeof organizationId !== "string") continue;
		for (const datasetId of REFRESHED_ON_REPLAY) {
			const [orgKey] = DATASET_REGISTRY[datasetId].queryOptionsFactory({
				organizationId,
			}).queryKey;
			const refreshKey = `${datasetId}:${organizationId}`;
			if (hashKey([orgKey]) === hashKey(key) && !refreshes.has(refreshKey)) {
				refreshes.set(
					refreshKey,
					refreshPreparedDatasets(queryClient, datasetId, organizationId),
				);
			}
		}
	}
	await Promise.all(refreshes.values());
}
